import React, { useState, useEffect } from 'react';
import ColumnSettings from './ColumnSettings';
import './DataTable.css';

/**
 * 汎用データテーブルコンポーネント
 * 
 * 機能:
 * - 列の表示/非表示、並び替え、リサイズ
 * - ソート
 * - ページネーション
 * - チェックボックス選択
 * - カスタムセルレンダラー
 * - LocalStorage自動保存
 */
const DataTable = ({
  // データと列定義
  columns,
  data,
  defaultColumns,
  
  // 列設定
  onColumnsChange,
  localStorageKey,
  
  // ソート
  sortConfig,
  onSortChange,
  
  // 選択
  selectedIds = [],
  onSelectionChange,
  idField = 'id',
  
  // ページネーション
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  
  // カスタムレンダリング
  renderCell,
  
  // 一括操作
  bulkActionButton,
  
  // その他
  emptyMessage = 'データがありません',
  className = ''
}) => {
  const [resizing, setResizing] = useState(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // 列幅リサイズの開始
  const handleResizeStart = (e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    const column = columns.find(col => col.id === columnId);
    setResizing({
      columnId: columnId,
      startX: e.clientX,
      startWidth: column?.width || 150
    });
  };

  // 列幅リサイズの処理
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff);
      
      onColumnsChange(prev => prev.map(col => 
        col.id === resizing.columnId ? { ...col, width: newWidth } : col
      ));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onColumnsChange]);

  // LocalStorageへの自動保存
  useEffect(() => {
    if (localStorageKey && columns) {
      localStorage.setItem(localStorageKey, JSON.stringify(columns));
    }
  }, [columns, localStorageKey]);

  // 列設定のリセット
  const handleResetColumns = () => {
    if (defaultColumns) {
      onColumnsChange(defaultColumns);
      if (localStorageKey) {
        localStorage.removeItem(localStorageKey);
      }
    }
  };

  // 全選択/全解除
  const handleToggleAll = () => {
    if (selectedIds.length === data.length && data.length > 0) {
      onSelectionChange([]);
    } else {
      onSelectionChange(data.map(item => item[idField]));
    }
  };

  // 個別選択
  const handleToggleSelection = (id) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  // ソート処理
  const handleSort = (columnId) => {
    const column = columns.find(col => col.id === columnId);
    if (!column?.sortable) return;

    onSortChange({
      column: columnId,
      order: sortConfig?.column === columnId && sortConfig?.order === 'desc' ? 'asc' : 'desc'
    });
  };

  // ヘッダー右クリックで列設定を開く
  const handleHeaderContextMenu = (e) => {
    e.preventDefault();
    setShowColumnSettings(true);
  };

  // 表示する列をフィルタ
  const visibleColumns = columns.filter(col => col.visible);

  // ページネーション情報コンポーネント
  const renderPagination = (position = 'bottom') => {
    if (totalItems === undefined) return null;
    
    // テーブル上部の場合、左側に一括操作ボタン、右側にページネーション情報を配置
    if (position === 'top' && bulkActionButton) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'nowrap',
          minWidth: 0
        }}>
          {/* 左側：一括操作ボタン */}
          <div style={{ flex: '0 0 auto' }}>
            {bulkActionButton}
          </div>
          
          {/* 右側：ページネーション情報 */}
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'nowrap',
            minWidth: 0
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', whiteSpace: 'nowrap' }}>
              <span>表示件数:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1);
                }}
                style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
              >
                <option value="20">20件</option>
                <option value="50">50件</option>
                <option value="100">100件</option>
                <option value="200">200件</option>
              </select>
            </div>

            <div style={{ fontSize: '14px', color: '#718096', whiteSpace: 'nowrap' }}>
              全{totalItems}件中 {(currentPage - 1) * pageSize + 1}~{Math.min(currentPage * pageSize, totalItems)}件を表示
            </div>

            <div style={{ display: 'flex', gap: '8px', whiteSpace: 'nowrap' }}>
              <button
                className="btn btn-secondary"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  padding: '4px 12px',
                  fontSize: '14px'
                }}
              >
                前へ
              </button>
              <span style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '0 8px',
                fontSize: '14px',
                color: '#718096'
              }}>
                {currentPage} / {Math.ceil(totalItems / pageSize)}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalItems / pageSize)}
                style={{
                  opacity: currentPage >= Math.ceil(totalItems / pageSize) ? 0.5 : 1,
                  cursor: currentPage >= Math.ceil(totalItems / pageSize) ? 'not-allowed' : 'pointer',
                  padding: '4px 12px',
                  fontSize: '14px'
                }}
              >
                次へ
              </button>
            </div>

            {/* 列設定ボタン */}
            <button
              className="btn btn-secondary"
              onClick={() => setShowColumnSettings(true)}
              style={{
                padding: '4px 12px',
                fontSize: '16px'
              }}
              title="列設定（右クリックでも開けます）"
            >
              ⚙️
            </button>
          </div>
        </div>
      );
    }
    
    // テーブル上部の場合（一括操作ボタンなし）、右寄せのページネーション情報と歯車ボタンを表示
    if (position === 'top') {
      return (
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          flexWrap: 'nowrap',
          minWidth: 0
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', whiteSpace: 'nowrap' }}>
            <span>表示件数:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(1);
              }}
              style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
            >
              <option value="20">20件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
              <option value="200">200件</option>
            </select>
          </div>

          <div style={{ fontSize: '14px', color: '#718096', whiteSpace: 'nowrap' }}>
            全{totalItems}件中 {(currentPage - 1) * pageSize + 1}~{Math.min(currentPage * pageSize, totalItems)}件を表示
          </div>

          <div style={{ display: 'flex', gap: '8px', whiteSpace: 'nowrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                padding: '4px 12px',
                fontSize: '14px'
              }}
            >
              前へ
            </button>
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '0 8px',
              fontSize: '14px',
              color: '#718096'
            }}>
              {currentPage} / {Math.ceil(totalItems / pageSize)}
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalItems / pageSize)}
              style={{
                opacity: currentPage >= Math.ceil(totalItems / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalItems / pageSize) ? 'not-allowed' : 'pointer',
                padding: '4px 12px',
                fontSize: '14px'
              }}
            >
              次へ
            </button>
          </div>

          {/* 列設定ボタン（上部のみ表示） */}
          <button
            className="btn btn-secondary"
            onClick={() => setShowColumnSettings(true)}
            style={{
              padding: '4px 12px',
              fontSize: '16px'
            }}
            title="列設定（右クリックでも開けます）"
          >
            ⚙️
          </button>
        </div>
      );
    }
    
    // テーブル下部の場合、右寄せのページネーション情報のみ（歯車ボタンなし）
    return (
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        alignItems: 'center', 
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        minWidth: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', whiteSpace: 'nowrap' }}>
          <span>表示件数:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
          >
            <option value="20">20件</option>
            <option value="50">50件</option>
            <option value="100">100件</option>
            <option value="200">200件</option>
          </select>
        </div>

        <div style={{ fontSize: '14px', color: '#718096', whiteSpace: 'nowrap' }}>
          全{totalItems}件中 {(currentPage - 1) * pageSize + 1}~{Math.min(currentPage * pageSize, totalItems)}件を表示
        </div>

        <div style={{ display: 'flex', gap: '8px', whiteSpace: 'nowrap' }}>
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              opacity: currentPage === 1 ? 0.5 : 1,
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              padding: '4px 12px',
              fontSize: '14px'
            }}
          >
            前へ
          </button>
          <span style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '0 8px',
            fontSize: '14px',
            color: '#718096'
          }}>
            {currentPage} / {Math.ceil(totalItems / pageSize)}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= Math.ceil(totalItems / pageSize)}
            style={{
              opacity: currentPage >= Math.ceil(totalItems / pageSize) ? 0.5 : 1,
              cursor: currentPage >= Math.ceil(totalItems / pageSize) ? 'not-allowed' : 'pointer',
              padding: '4px 12px',
              fontSize: '14px'
            }}
          >
            次へ
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`data-table-container ${className}`}>
      {/* 列設定ボタンを削除し、ヘッダー右クリックで設定を開くように変更 */}

      {/* 列設定コンポーネント */}
      <ColumnSettings
        columns={columns}
        onColumnsChange={onColumnsChange}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
        onReset={handleResetColumns}
        isOpen={showColumnSettings}
        onToggle={() => setShowColumnSettings(!showColumnSettings)}
      />

      {/* ページネーション情報（テーブルの上） */}
      {totalItems !== undefined && (
        <div style={{ marginBottom: '12px' }}>
          {renderPagination('top')}
        </div>
      )}


      {/* テーブル */}
      <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
        <table className="data-table">
          <thead onContextMenu={handleHeaderContextMenu}>
            <tr>
              {visibleColumns.map((column, columnIndex) => {
                const thStyle = {
                  width: column.width ? `${column.width}px` : 'auto',
                  userSelect: resizing ? 'none' : 'auto'
                };

                // チェックボックス列
                if (column.id === 'checkbox') {
                  return (
                    <th key={column.id} style={{ ...thStyle, textAlign: 'center' }}>
                      {onSelectionChange && (
                        <input
                          type="checkbox"
                          checked={selectedIds.length === data.length && data.length > 0}
                          onChange={handleToggleAll}
                          style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                      )}
                      {!column.fixed && (
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleResizeStart(e, column.id)}
                        />
                      )}
                    </th>
                  );
                }

                // ソート可能な列
                if (column.sortable) {
                  return (
                    <th
                      key={column.id}
                      onClick={() => handleSort(column.id)}
                      style={{ ...thStyle, cursor: 'pointer' }}
                    >
                      {column.label}{' '}
                      {sortConfig?.column === column.id && (sortConfig?.order === 'asc' ? '▲' : '▼')}
                      {!column.fixed && (
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleResizeStart(e, column.id)}
                        />
                      )}
                    </th>
                  );
                }

                // 通常の列
                return (
                  <th key={column.id} style={thStyle}>
                    {column.label}
                    {!column.fixed && (
                      <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeStart(e, column.id)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr key={row[idField] || rowIndex}>
                  {visibleColumns.map(column => {
                    const cellStyle = { width: column.width ? `${column.width}px` : 'auto' };

                    // チェックボックス列
                    if (column.id === 'checkbox') {
                      return (
                        <td key={column.id} style={{ ...cellStyle, textAlign: 'center' }}>
                          {onSelectionChange && (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row[idField])}
                              onChange={() => handleToggleSelection(row[idField])}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          )}
                        </td>
                      );
                    }

                    // カスタムレンダラー
                    if (renderCell) {
                      const customContent = renderCell(row, column, cellStyle);
                      if (customContent !== undefined) {
                        return customContent;
                      }
                    }

                    // デフォルトレンダリング
                    return (
                      <td key={column.id} style={cellStyle}>
                        {row[column.id] ?? '-'}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ページネーション情報（テーブルの下） - 1ページで収まる場合は非表示 */}
      {totalItems !== undefined && totalItems > pageSize && (
        <div style={{ marginTop: '12px' }}>
          {renderPagination('bottom')}
        </div>
      )}
    </div>
  );
};

export default DataTable;
